import { useState } from 'react';
import type { Angle } from '@attack100/shared';

interface AngleGalleryProps {
  angles: Angle[];
  onActivate: (angle: Angle) => void;
  onViewDetails: (angle: Angle) => void;
  onDelete: (angle: Angle) => void;
}

type SortOption = 'relevance' | 'number' | 'persona';
type FilterPersona = 'all' | 'architect' | 'economist' | 'psychologist' | 'storyteller' | 'operator' | 'strategist' | 'builder' | 'connector' | 'scientist' | 'red-team';
type FilterStatus = 'all' | 'generated' | 'active';

export function AngleGallery({ angles, onActivate, onViewDetails, onDelete }: AngleGalleryProps) {
  const [sortBy, setSortBy] = useState<SortOption>('relevance');
  const [filterPersona, setFilterPersona] = useState<FilterPersona>('all');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Apply filters and sorting
  const filteredAngles = angles
    .filter(angle => {
      // Status filter
      if (filterStatus !== 'all' && angle.status !== filterStatus) return false;

      // Persona filter
      if (filterPersona !== 'all' && angle.persona !== filterPersona) return false;

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return angle.title.toLowerCase().includes(query) ||
               angle.description.toLowerCase().includes(query);
      }

      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'relevance':
          return (b.relevance_score || 0) - (a.relevance_score || 0);
        case 'number':
          return a.angle_number - b.angle_number;
        case 'persona':
          return (a.persona || '').localeCompare(b.persona || '');
        default:
          return 0;
      }
    });

  const getPersonaColor = (persona?: string) => {
    const colors: Record<string, string> = {
      'architect': '#3b82f6',
      'economist': '#10b981',
      'psychologist': '#8b5cf6',
      'storyteller': '#f59e0b',
      'operator': '#6366f1',
      'strategist': '#ef4444',
      'builder': '#14b8a6',
      'connector': '#ec4899',
      'scientist': '#06b6d4',
      'red-team': '#dc2626'
    };
    return colors[persona || ''] || '#6b7280';
  };

  const getPersonaName = (persona?: string) => {
    const names: Record<string, string> = {
      'architect': 'The Architect',
      'economist': 'The Economist',
      'psychologist': 'The Psychologist',
      'storyteller': 'The Storyteller',
      'operator': 'The Operator',
      'strategist': 'The Strategist',
      'builder': 'The Builder',
      'connector': 'The Connector',
      'scientist': 'The Scientist',
      'red-team': 'The Red Team'
    };
    return names[persona || ''] || persona || 'Unknown';
  };

  const generatedCount = angles.filter(a => a.status === 'generated').length;
  const activeCount = angles.filter(a => a.status === 'active').length;

  return (
    <div>
      {/* Header Stats */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div className="badge badge-primary" style={{ padding: '0.5rem 1rem' }}>
          {angles.length} Total Angles
        </div>
        <div className="badge badge-success" style={{ padding: '0.5rem 1rem' }}>
          {activeCount} Active
        </div>
        <div style={{ padding: '0.5rem 1rem', background: 'var(--gray-100)', borderRadius: '9999px', fontSize: '0.875rem' }}>
          {generatedCount} Available
        </div>
      </div>

      {/* Controls */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          {/* Search */}
          <div>
            <label className="label" style={{ marginBottom: '0.25rem' }}>Search</label>
            <input
              type="text"
              className="input"
              placeholder="Search angles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ fontSize: '0.875rem' }}
            />
          </div>

          {/* Sort */}
          <div>
            <label className="label" style={{ marginBottom: '0.25rem' }}>Sort By</label>
            <select
              className="input"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="relevance">Relevance</option>
              <option value="number">Angle Number</option>
              <option value="persona">Persona</option>
            </select>
          </div>

          {/* Filter Persona */}
          <div>
            <label className="label" style={{ marginBottom: '0.25rem' }}>Persona</label>
            <select
              className="input"
              value={filterPersona}
              onChange={(e) => setFilterPersona(e.target.value as FilterPersona)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="all">All Personas</option>
              <option value="architect">The Architect</option>
              <option value="economist">The Economist</option>
              <option value="psychologist">The Psychologist</option>
              <option value="storyteller">The Storyteller</option>
              <option value="operator">The Operator</option>
              <option value="strategist">The Strategist</option>
              <option value="builder">The Builder</option>
              <option value="connector">The Connector</option>
              <option value="scientist">The Scientist</option>
              <option value="red-team">The Red Team</option>
            </select>
          </div>

          {/* Filter Status */}
          <div>
            <label className="label" style={{ marginBottom: '0.25rem' }}>Status</label>
            <select
              className="input"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="all">All Status</option>
              <option value="generated">Available</option>
              <option value="active">Active</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '1rem' }}>
        Showing {filteredAngles.length} of {angles.length} angles
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-2">
        {filteredAngles.map(angle => (
          <div key={angle.id} className="card" style={{ cursor: 'pointer', transition: 'all 0.2s' }}
            onClick={() => onViewDetails(angle)}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span
                  className="badge"
                  style={{
                    background: getPersonaColor(angle.persona),
                    color: 'white',
                    fontSize: '0.7rem'
                  }}
                >
                  {getPersonaName(angle.persona)}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                  #{angle.angle_number}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {angle.status === 'active' && (
                  <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>
                    Active
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Delete "${angle.title}"?\n\nThis action cannot be undone.`)) {
                      onDelete(angle);
                    }
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--gray-400)',
                    cursor: 'pointer',
                    padding: '0.25rem',
                    fontSize: '1rem',
                    lineHeight: 1,
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--error)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--gray-400)'}
                  title="Delete angle"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Title */}
            <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '0.5rem', lineHeight: '1.4' }}>
              {angle.title}
            </h4>

            {/* Description Preview */}
            <p style={{
              fontSize: '0.8125rem',
              color: 'var(--gray-600)',
              lineHeight: '1.5',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {angle.description}
            </p>

            {/* Footer */}
            <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--gray-200)' }}>
              {angle.status === 'generated' ? (
                <button
                  className="btn btn-primary"
                  style={{ width: '100%', fontSize: '0.8125rem', padding: '0.5rem' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onActivate(angle);
                  }}
                >
                  ⚡ Activate This Angle
                </button>
              ) : (
                <button
                  className="btn btn-secondary"
                  style={{ width: '100%', fontSize: '0.8125rem', padding: '0.5rem' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails(angle);
                  }}
                >
                  💬 Open Workspace
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredAngles.length === 0 && (
        <div className="empty-state">
          <h3>No angles found</h3>
          <p>Try adjusting your filters or search query</p>
        </div>
      )}
    </div>
  );
}
